/**
 * Marketplace endpoint façade.
 *
 * Wraps the underlying real / mock implementations from
 * `@/api/client` so that the six "Official Products" pre-seeded in
 * `@/data/defaultProducts` always appear in:
 *
 *   - the listings grid (`list`) — even on a freshly-deployed engine
 *     whose server-side log has zero published listings.
 *   - detail lookups (`get`) — so the cards link to a working
 *     detail page.
 *
 * The locale is read at call time from the active i18n config so the
 * official products' name / tagline / description render in the
 * caller's language. Server-published listings (which carry their
 * own author-signed strings) pass through unchanged.
 */

import { api } from '../client';
import i18n, { type Locale } from '@/i18n/config';
import {
  getOfficialListing,
  getOfficialListings,
  OFFICIAL_PRODUCT_ID_SET,
} from '@/data/defaultProducts';
import type {
  MarketplaceFilters,
  MarketplaceListing,
  MarketplaceReview,
} from '../types';

function activeLocale(): Locale {
  return (i18n.language as Locale) ?? 'en';
}

function applyFilters(
  listings: MarketplaceListing[],
  f: MarketplaceFilters,
): MarketplaceListing[] {
  let out = listings.slice();
  if (f.search) {
    const q = f.search.toLowerCase();
    out = out.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.tagline.toLowerCase().includes(q) ||
        l.description.toLowerCase().includes(q),
    );
  }
  if (f.domain) {
    out = out.filter((l) =>
      typeof l.domain === 'string' && typeof f.domain === 'string'
        ? l.domain === f.domain
        : false,
    );
  }
  if (f.certification) {
    out = out.filter((l) => l.certification === f.certification);
  }
  if (typeof f.min_rating === 'number') {
    out = out.filter((l) => l.rating >= f.min_rating!);
  }
  if (typeof f.max_price_usd === 'number') {
    out = out.filter((l) => l.price_usd <= f.max_price_usd!);
  }
  return out;
}

async function list(
  filters: MarketplaceFilters = {},
): Promise<MarketplaceListing[]> {
  const locale = activeLocale();
  const officials = applyFilters(getOfficialListings(locale), filters);
  const fromBackend = await api.listMarketplace(filters);
  // Drop any backend rows that share an id with an official product so
  // we never double-count after a publish round-trip or a post-deploy
  // backend re-seed.
  const filteredBackend = fromBackend.filter(
    (l) => !OFFICIAL_PRODUCT_ID_SET.has(l.root_id),
  );
  return [...officials, ...filteredBackend];
}

async function get(
  id: string,
): Promise<{ listing: MarketplaceListing; reviews: MarketplaceReview[] } | null> {
  if (OFFICIAL_PRODUCT_ID_SET.has(id)) {
    const listing = getOfficialListing(id, activeLocale());
    if (listing) return { listing, reviews: [] };
  }
  return api.getMarketplaceListing(id);
}

export const marketplace = {
  list,
  get,
  publish: api.publishToMarketplace,
  reset: api.resetMarketplace,
};
